import { useNavigate } from "react-router-dom";
import './CoursePolicies.css';

export default function CoursePolicies() {

    const navigate = useNavigate();

    const handleRedirect = () => {
        navigate("/terms-conditions");
    };

    return (
        <>
            <section id="course-policies-section">
                <div className="course-policies-container">
                    <div className="course-policies-header">
                        <h2 className="section-title">Course Policies</h2>
                        <p className="section-subtitle">Understanding our course structure and policies</p>
                    </div>

                    <div className="course-policies-content">
                        {/* Section 1: Enrollment & Scheduling */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">1. Enrollment & Scheduling</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>Classes are scheduled in advance based on student and teacher availability.</li>
                                    <li>Students must confirm their chosen course type (Crash, Quarter, or Certification) and selected subject(s) at the time of enrollment.</li>
                                    <li>Dual Program and All-in-One Program require pre-approval for subject combinations.</li>
                                    <li>Music Production, Dual Program, and All-in-One cannot be chosen as subjects within other multi-subject courses.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 2: Attendance & Rescheduling */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">2. Attendance & Rescheduling</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>Regular attendance is essential for steady progress.</li>
                                    <li>Any class rescheduling requests must be made at least 6 hours in advance.</li>
                                    <li>If a class is cancelled at the time of the scheduled class or within 30 minutes before it starts, it will be treated as an absent class due to last-minute cancellation and cannot be rescheduled.</li>
                                    <li>Missed classes without prior notice will be considered forfeited and cannot be rescheduled.</li>
                                    <li>Classes cancelled by the academy will be rescheduled at no extra cost.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 3: Class Duration & Format */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">3. Class Duration & Format</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>Each session typically lasts 50–60 minutes, even if the brochure mentions 60 minutes.</li>
                                    <li>Classes are conducted online on the student dashboard on the website, unless otherwise specified.</li>
                                    <li>Group or individual class formats are determined by the chosen program and level.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 4: Progress Tracking & Exams */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">4. Progress Tracking & Exams</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>All Quarter, Certification, Dual, and All-in-One Program students receive full access to the student dashboard for tracking progress.</li>
                                    <li>Crash Course students receive partial dashboard benefits.</li>
                                    <li>Students can prepare for international graded exams (Trinity, Rock School, ABRSM, Bhatkhande, etc.) based on the course.</li>
                                    <li>Exam fees (if applicable) are separate from course fees.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 5: Teacher & Academy Communication */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">5. Teacher & Academy Communication</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>All communication regarding classes or technical issues must go through the official academy channels.</li>
                                    <li>Students/parents are requested not to exchange personal contact details with teachers.</li>
                                    <li>For any issues, students may contact the academy during working hours.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 6: Certification & Completion */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">6. Certification & Completion</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>Students completing Certification Courses receive an official Certificate of Completion from the academy.</li>
                                    <li>Dual Program and All-in-One students receive combined certification covering all their chosen subjects.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 7: Conduct & Discipline */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">7. Conduct & Discipline</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>Students are expected to maintain respectful behavior towards teachers and peers.</li>
                                    <li>Repeated misconduct or violation of policies may result in suspension or removal from the program without refund.</li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 8: Teacher / Subject Shift */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">8. Teacher / Subject Shift</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <p className="course-policy-intro">
                                    At Harshita School of Music, we understand that every student's learning needs are unique. To ensure the best possible experience, the following policies apply:
                                </p>
                                
                                <div className="course-policy-subsection">
                                    <h4 className="course-policy-subsection-title">Requesting a Different Teacher</h4>
                                    <p>A student may request a change of teacher if:</p>
                                    <ul className="course-policy-sublist">
                                        <li>They do not understand the current teacher's training approach or teaching style.</li>
                                        <li>They feel the teacher is not aligned with their learning needs, pace, or goals.</li>
                                    </ul>
                                </div>

                                <div className="course-policy-subsection">
                                    <h4 className="course-policy-subsection-title">Requesting a Subject Change</h4>
                                    <p>A student may request to shift to a different subject if:</p>
                                    <ul className="course-policy-sublist">
                                        <li>They face physical limitations preventing them from continuing the current subject (e.g., vocal strain or instrument-related discomfort).</li>
                                        <li>They wish to pursue a subject better suited to their interests or career goals (subject to availability and academy approval).</li>
                                    </ul>
                                </div>

                                <div className="course-policy-subsection">
                                    <h4 className="course-policy-subsection-title">Academy-Initiated Teacher Reassignment</h4>
                                    <p>The academy reserves the right to reassign teachers at any time if:</p>
                                    <ul className="course-policy-sublist">
                                        <li>A different teacher is deemed more suitable for the student's progress.</li>
                                        <li>Scheduling changes or staff transitions occur.</li>
                                        <li>Any inappropriate activity or unprofessional behavior is detected from either the teacher or the student.</li>
                                    </ul>
                                </div>

                                <div className="course-policy-subsection">
                                    <h4 className="course-policy-subsection-title">Conditions for Subject Changes</h4>
                                    <ul className="course-policy-sublist">
                                        <li>Subject changes are permitted only within the first 25% of the course duration, unless there are valid medical reasons or exceptional circumstances approved by the academy.</li>
                                        <li>Any teacher or subject change is dependent on teacher availability and batch capacity.</li>
                                    </ul>
                                </div>

                                <div className="course-policy-subsection">
                                    <h4 className="course-policy-subsection-title">Fees & Adjustments</h4>
                                    <ul className="course-policy-sublist">
                                        <li>If the new subject chosen has a lower fee, the fee difference will not be refunded.</li>
                                        <li>If the new subject has a higher fee, the student must deposit the extra amount before the change is processed.</li>
                                        <li>The academy will provide full, clear details of any fee adjustments in such cases.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Section 9: Policy Updates */}
                        <div className="course-policy-section">
                            <div className="course-policy-section-header">
                                <h3 className="course-policy-section-title">9. Policy Updates</h3>
                            </div>
                            <div className="course-policy-section-content">
                                <ul className="course-policy-list">
                                    <li>The academy reserves the right to modify policies or course structures at any time to ensure quality and fairness.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* Terms & Conditions Button */}
                    <div className="course-policy-cta">
                        <h3 className="cta-title">READ ALL COURSE POLICY HERE</h3>
                        <button className="terms-conditions-button" onClick={handleRedirect}>
                            Terms & Conditions
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
}